// =============================================================================
// Portal Subscription Page — current plan + change/cancel + Stripe portal SSO
// -----------------------------------------------------------------------------
// Talks to:
//   • GET    /api/v1/subscription                — current sub (or null)
//   • GET    /api/v1/organizations/plans         — available plans catalog
//   • GET    /api/v1/organizations/current/connections — detect Shopify link
//   • POST   /api/v1/subscription                — create/upgrade
//   • POST   /api/v1/subscription/cancel         — schedule cancel / cancel now
//   • POST   /api/v1/subscription/billing-portal — Stripe Customer Portal SSO
//
// Provider routing rules:
//   • Org has an ACTIVE Shopify Connection → ALL plan management happens
//     inside the embedded Shopify app (Managed Pricing / Billing API).
//     The plan picker is hidden and a deep-link card is shown instead.
//     The server enforces the same rule (changePlan throws 403 for these
//     orgs); the UI block is defensive UX.
//   • Otherwise → Stripe-billed. Plan picker + Stripe Customer Portal.
// =============================================================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import type {
  CancelSubscriptionResponse,
  CreateSubscriptionResponse,
  PlanSummaryDto,
  SubscriptionDto,
} from '@tkpod/shared';
import { Button, Card, CardHeader, Modal, ModalFooter, Spinner } from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PlanCatalogResponse {
  data: Array<{
    id: string;
    name: string;
    slug: string;
    monthlyPrice: string;
    yearlyPrice: string | null;
    discountPercentage: string;
    storageLimit: number | null;
    exportsLimit: number | null;
    storesLimit: number | null;
    usersLimit: number | null;
    features: string[];
  }>;
}

interface ConnectionDto {
  id: string;
  type: 'SHOPIFY' | 'WOOCOMMERCE' | string;
  status: 'ACTIVE' | 'DISCONNECTED' | 'ERROR' | string;
  externalDomain: string | null;
  name?: string | null;
}

interface ConnectionsResponse {
  data: ConnectionDto[];
}

/**
 * Build the deep-link URL that takes the merchant straight into the
 * embedded TK POD app's plan page in their Shopify admin.
 *
 * The Shopify Admin URL format for an embedded-app deep link is:
 *   https://{shop}.myshopify.com/admin/apps/{handle}/{route}
 *
 * `handle` is configurable via VITE_SHOPIFY_APP_HANDLE so the same Portal
 * build can target dev / prod app installations without a code change.
 * Falls back to `/admin/apps` (generic apps list) when the handle is not
 * configured — still better than no link at all.
 */
function buildShopifyPlanDeepLink(shopDomain: string | null): string | null {
  if (!shopDomain) return null;
  const handle = import.meta.env.VITE_SHOPIFY_APP_HANDLE as string | undefined;
  if (!handle) return `https://${shopDomain}/admin/apps`;
  return `https://${shopDomain}/admin/apps/${handle}/app/plan`;
}

function formatPrice(decimalString: string | null): string {
  if (!decimalString) return '—';
  const value = Number.parseFloat(decimalString);
  if (!Number.isFinite(value)) return decimalString;
  if (value === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPlanCents(plan: PlanSummaryDto): string {
  if (plan.monthlyPriceUsdCents === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(plan.monthlyPriceUsdCents / 100);
}

function statusTone(status: SubscriptionDto['status']): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'ACTIVE':
      return {
        label: 'Active',
        className:
          'bg-emerald-50 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
      };
    case 'TRIALING':
      return {
        label: 'Trialing',
        className:
          'bg-sky-50 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
      };
    case 'PAST_DUE':
      return {
        label: 'Past due',
        className:
          'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
      };
    case 'CANCELLED':
    case 'INCOMPLETE_EXPIRED':
      return {
        label: status === 'CANCELLED' ? 'Cancelled' : 'Incomplete',
        className: 'bg-rose-50 text-rose-700 dark:bg-rose-900 dark:text-rose-200',
      };
    case 'UNPAID':
      return {
        label: 'Unpaid',
        className: 'bg-rose-50 text-rose-700 dark:bg-rose-900 dark:text-rose-200',
      };
    default:
      return {
        label: status,
        className:
          'bg-surface-tertiary text-text-secondary',
      };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PortalSubscriptionPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [pendingPlanSlug, setPendingPlanSlug] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [banner, setBanner] = useState<
    { tone: 'success' | 'error' | 'info'; message: string } | null
  >(null);

  const subQuery = useQuery<{ subscription: SubscriptionDto | null }>({
    queryKey: ['subscription'],
    queryFn: () =>
      api.get<{ subscription: SubscriptionDto | null }>('/api/v1/subscription'),
  });

  const plansQuery = useQuery<PlanCatalogResponse>({
    queryKey: ['plans', 'catalog'],
    queryFn: () =>
      api.get<PlanCatalogResponse>('/api/v1/organizations/plans'),
    staleTime: 5 * 60_000,
  });

  const connectionsQuery = useQuery<ConnectionsResponse>({
    queryKey: ['connections', 'current'],
    queryFn: () =>
      api.get<ConnectionsResponse>('/api/v1/organizations/current/connections'),
    staleTime: 60_000,
  });

  const shopifyConnection = useMemo(() => {
    const conns = connectionsQuery.data?.data ?? [];
    return (
      conns.find((c) => c.type === 'SHOPIFY' && c.status === 'ACTIVE') ?? null
    );
  }, [connectionsQuery.data]);

  // An org is "Shopify-billed" iff it has an active Shopify connection.
  // Even on the free plan today, ALL future plan transitions for these
  // orgs must go through Shopify Managed Pricing / Billing API. Surfacing
  // a plan picker would let them initiate a Stripe Checkout that the
  // backend would then reject with 403 — confusing UX. Hide it instead.
  const isShopifyBilled = shopifyConnection !== null;
  const shopifyPlanDeepLink = buildShopifyPlanDeepLink(
    shopifyConnection?.externalDomain ?? null,
  );

  const upgradeMutation = useMutation({
    mutationFn: (planSlug: string) =>
      api.post<CreateSubscriptionResponse>('/api/v1/subscription', {
        planSlug,
        returnUrl: `${window.location.origin}/billing?tab=subscription&changed=1`,
      }),
    onSuccess: (data) => {
      // Provider-specific navigation:
      //   - SHOPIFY → confirmationUrl is the recurring-charge approval page
      //   - STRIPE  → confirmationUrl is the Stripe Checkout session
      // We always redirect when the URL is present; a missing URL means the
      // upgrade was applied in-place (e.g. proration on an existing Stripe
      // subscription) and we just refetch.
      if (data.confirmationUrl) {
        window.location.href = data.confirmationUrl;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setPlanPickerOpen(false);
      setPendingPlanSlug(null);
      setBanner({
        tone: 'success',
        message: 'Plan updated.',
      });
    },
    onError: (err) => {
      setPendingPlanSlug(null);
      setBanner({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Plan change failed.',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (immediate: boolean) =>
      api.post<CancelSubscriptionResponse>('/api/v1/subscription/cancel', {
        immediate,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['subscription'], { subscription: data.subscription });
      setCancelOpen(false);
      setBanner({
        tone: 'success',
        message: data.subscription.cancelAtPeriodEnd
          ? 'Cancellation scheduled — your plan stays active until the end of the current period.'
          : 'Subscription cancelled.',
      });
    },
    onError: (err) =>
      setBanner({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Cancellation failed.',
      }),
  });

  const portalMutation = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/v1/subscription/billing-portal', {
        returnUrl: `${window.location.origin}/billing?tab=subscription`,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) =>
      setBanner({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not open billing portal.',
      }),
  });

  const subscription = subQuery.data?.subscription ?? null;
  const plans = useMemo(() => plansQuery.data?.data ?? [], [plansQuery.data]);
  const tone = subscription ? statusTone(subscription.status) : null;

  return (
    <div className="space-y-6">
      {banner && (
        <div
          role="status"
          className={
            banner.tone === 'success'
              ? 'rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100'
              : banner.tone === 'error'
                ? 'rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100'
                : 'rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100'
          }
        >
          <div className="flex items-start justify-between gap-3">
            <span>{banner.message}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-xs font-medium underline opacity-80 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Current plan card */}
      <Card>
        <CardHeader title="Current plan" />
        <div className="space-y-4 px-4 pb-5 sm:px-5">
          {subQuery.isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Spinner />
            </div>
          ) : subQuery.isError ? (
            <div className="space-y-2">
              <p className="text-sm text-rose-600">
                {subQuery.error instanceof Error
                  ? subQuery.error.message
                  : 'Unable to load subscription.'}
              </p>
              <Button variant="secondary" onClick={() => subQuery.refetch()}>
                Retry
              </Button>
            </div>
          ) : !subscription ? (
            <div className="space-y-3">
              <p className="text-sm text-text-primary">
                You're on the <span className="font-semibold">Free</span> plan.
              </p>
              {isShopifyBilled ? (
                <ShopifyManagedPricingCard
                  shopDomain={shopifyConnection?.externalDomain ?? null}
                  deepLink={shopifyPlanDeepLink}
                />
              ) : (
                <>
                  <p className="text-xs text-text-secondary">
                    Upgrade to unlock higher storage, more stores, and priority
                    support. You can cancel any time from this page.
                  </p>
                  <Button onClick={() => setPlanPickerOpen(true)}>
                    Choose a plan
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-2xl font-semibold text-text-primary">
                  {subscription.plan.name}
                </span>
                {tone && (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${tone.className}`}
                  >
                    {tone.label}
                  </span>
                )}
                <span className="text-xs uppercase tracking-wide text-text-tertiary">
                  Billed via {subscription.provider === 'SHOPIFY' ? 'Shopify' : 'Stripe'}
                </span>
              </div>
              <p className="text-sm text-text-secondary">
                {formatPlanCents(subscription.plan)} / month
                {subscription.cancelAtPeriodEnd
                  ? ` — cancels on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                  : ` — renews on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
              </p>
              {subscription.trialEnd && new Date(subscription.trialEnd) > new Date() && (
                <p className="text-xs text-sky-700 dark:text-sky-300">
                  Trial ends {new Date(subscription.trialEnd).toLocaleDateString()}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {subscription.provider === 'SHOPIFY' || isShopifyBilled ? (
                  shopifyPlanDeepLink && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        window.location.href = shopifyPlanDeepLink;
                      }}
                    >
                      Manage plan in Shopify admin
                    </Button>
                  )
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => setPlanPickerOpen(true)}
                    disabled={subscription.status === 'CANCELLED'}
                  >
                    Change plan
                  </Button>
                )}
                {subscription.provider === 'STRIPE' && !isShopifyBilled && (
                  <Button
                    variant="secondary"
                    onClick={() => portalMutation.mutate()}
                    disabled={portalMutation.isPending}
                  >
                    {portalMutation.isPending
                      ? 'Opening…'
                      : 'Manage billing in Stripe portal'}
                  </Button>
                )}
                {!subscription.cancelAtPeriodEnd &&
                  subscription.status !== 'CANCELLED' &&
                  subscription.provider !== 'SHOPIFY' &&
                  !isShopifyBilled && (
                    <Button
                      variant="ghost"
                      onClick={() => setCancelOpen(true)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancel subscription
                    </Button>
                  )}
              </div>

              {(subscription.provider === 'SHOPIFY' || isShopifyBilled) && (
                <p className="text-xs text-text-tertiary">
                  Billing is processed by Shopify. Plan changes, invoice
                  history, and cancellations all live inside your Shopify
                  admin.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Plan picker — never opens for Shopify-billed orgs (defensive UX;
          backend enforces the same rule with a 403). */}
      <Modal
        open={planPickerOpen && !isShopifyBilled}
        onClose={() =>
          !upgradeMutation.isPending && setPlanPickerOpen(false)
        }
        title="Choose a plan"
        description={
          subscription
            ? 'Upgrades take effect immediately (Stripe pro-rates remaining time). Downgrades apply at the end of the current period.'
            : 'Pick a plan to continue. You can change or cancel any time.'
        }
        size="xl"
      >
        {plansQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : plansQuery.isError ? (
          <div className="space-y-2">
            <p className="text-sm text-rose-600">
              {plansQuery.error instanceof Error
                ? plansQuery.error.message
                : 'Unable to load plans.'}
            </p>
            <Button variant="secondary" onClick={() => plansQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => {
              const isCurrent = subscription?.plan.slug === plan.slug;
              const loading =
                upgradeMutation.isPending && pendingPlanSlug === plan.slug;
              return (
                <div
                  key={plan.slug}
                  className={`flex flex-col rounded-lg border p-4 ${
                    isCurrent
                      ? 'border-brand-600 bg-brand-50 dark:border-brand-500 dark:bg-brand-950'
                      : 'border-border'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-text-primary">
                      {plan.name}
                    </p>
                    {isCurrent && (
                      <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-2xl font-semibold text-text-primary">
                    {formatPrice(plan.monthlyPrice)}
                    <span className="ml-1 text-xs font-normal text-text-secondary">
                      / month
                    </span>
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-text-secondary">
                    {plan.storesLimit !== null && (
                      <li>
                        Up to {plan.storesLimit} store
                        {plan.storesLimit === 1 ? '' : 's'}
                      </li>
                    )}
                    {plan.usersLimit !== null && (
                      <li>
                        Up to {plan.usersLimit} user
                        {plan.usersLimit === 1 ? '' : 's'}
                      </li>
                    )}
                    {plan.features.slice(0, 4).map((feature) => (
                      <li key={feature}>• {feature}</li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-4">
                    <Button
                      fullWidth
                      variant={isCurrent ? 'secondary' : 'primary'}
                      disabled={isCurrent || loading || upgradeMutation.isPending}
                      onClick={() => {
                        setPendingPlanSlug(plan.slug);
                        upgradeMutation.mutate(plan.slug);
                      }}
                    >
                      {isCurrent
                        ? 'Current plan'
                        : loading
                          ? 'Redirecting…'
                          : 'Select plan'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setPlanPickerOpen(false)}
            disabled={upgradeMutation.isPending}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel confirmation — never opens for Shopify-billed orgs */}
      <Modal
        open={cancelOpen && !isShopifyBilled}
        onClose={() => !cancelMutation.isPending && setCancelOpen(false)}
        title="Cancel subscription?"
        description={
          subscription?.provider === 'SHOPIFY'
            ? 'Cancellation is processed by Shopify. Your plan stays active until the end of the current period.'
            : "We'll set the subscription to cancel at period end so you keep access until your renewal date. You can also choose to cancel immediately."
        }
        size="sm"
      >
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setCancelOpen(false)}
            disabled={cancelMutation.isPending}
          >
            Keep subscription
          </Button>
          {subscription?.provider !== 'SHOPIFY' && (
            <Button
              variant="danger"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate(true)}
            >
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel immediately'}
            </Button>
          )}
          <Button
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate(false)}
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Cancel at period end'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ShopifyManagedPricingCard({
  shopDomain,
  deepLink,
}: {
  shopDomain: string | null;
  deepLink: string | null;
}) {
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100">
      <p className="font-semibold">Plans are managed in Shopify</p>
      <p className="mt-1 text-xs">
        This organization is connected to{' '}
        <span className="font-mono">{shopDomain ?? 'your Shopify store'}</span>.
        All Transfer Kingdom POD plan changes, invoices, and cancellations
        happen inside the Shopify admin so your subscription stays in sync
        with Shopify billing.
      </p>
      {deepLink && (
        <div className="mt-3">
          <a
            href={deepLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"
          >
            Open in Shopify admin
            <span aria-hidden>↗</span>
          </a>
        </div>
      )}
    </div>
  );
}
